import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function SpotTenderConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);
    // Keep identity stable to avoid re-renders/refetches in children
    const removeContextItems = useMemo(() => ['Print', 'Copy', 'Create'], []);

    // Centralize CRUD route for consistency and reuse
    const CRUD_ROUTE = 'tenders/TenderConfigCrud';

    //===========================================================================================
    // openSpotTenderCrud:
    // - Stable (useCallback) handler used by MTable actions to open the CRUD tab.
    // - Writes 'operation' to global state and, for non-create ops, persists 'TenderLine'.
    // - Validates row presence for View/Edit; shows a localized error if missing.
    // - Builds a safe tab label using RequestForQuotes (falls back to empty string).
    // - Uses a centralized CRUD route constant to maintain consistency.
    //============================================================================================
    const openSpotTenderCrud = useCallback(
        (operation, row, label) => {
            // Update global state with the current operation
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

            // For View/Edit, ensure a row/spotTender data is selected; show error if not
            if (operation !== 'Create') {
                if (!row) { //If no row is selected, show error message
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                // Persist the selected TenderLine in global state for access in the CRUD tab
                dispatch(M_StateActions.addM_State({ key: 'TenderLine', value: row }));
            }

            // Construct the tab label, safely handling missing RequestForQuotes
            const tabLabel = operation === 'Create'
                ? label
                : `${label} "${row?.RequestForQuotes ?? ''}"`;

            // Invoke the callback to create/open the CRUD tab with the constructed label
            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, toastMessages, t]
    );

    //===========================================================================================
    // replaceFunctions:
    // - Memoized Map passed to MTable to resolve context actions without recreating on each render.
    // - Uses the stable openSpotTenderCrud callback and localized labels.
    // - Stable identity reduces unnecessary re-renders and duplicate API calls downstream.
    //============================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openSpotTenderCrud('View', row, t('LabelViewTender'))],
                ['Edit', (row) => openSpotTenderCrud('Edit', row, t('LabelEditTender'))],
            ]),
        [openSpotTenderCrud, t]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>{t("SpotTenders")}</div>
                    <div className='labelSubtitleSettings'>{t("InfoSpotTenders")}</div>
                </div>
            </div>
            <MTable // Dynamic table for Spot Tender configuration
                filtersOnTable
                entityName={'Tenders'} // Entity name for the table, Tenders table
                roleName={'Tenders'} //Module name for permissions
                rolePermissions={roles?.map(vl => vl.Value)} // Permissions for the current user
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                FILTER={"TenderTypeId eq 1"} // Filter for Spot Tenders, since we are on the Spot Tender Config page
                replaceFunctions={replaceFunctions} // Custom action handlers
                tabsRailWidth={350}
            />
        </div>
    );
}

SpotTenderConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
